import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, of, timeout } from 'rxjs';

import { AuthService } from './auth.service';
import { AuthStore } from './auth.store';
import { resolvePanelHomePath } from './panel-permissions';

const resolveHomeUrlTree = (authStore: AuthStore, router: Router): UrlTree => {
    return router.parseUrl(resolvePanelHomePath((permission) => authStore.can(permission)));
};

const resolveAccessGuard = (permissions: string[] | undefined) => {
    const authStore = inject(AuthStore);
    const authService = inject(AuthService);
    const router = inject(Router);

    const hasPermissionAccess = () => {
        if (!permissions?.length) {
            return true;
        }

        return authStore.can(permissions);
    };

    const denyAccess = () => resolveHomeUrlTree(authStore, router);

    if (authStore.isAuthenticated()) {
        return hasPermissionAccess() ? true : denyAccess();
    }

    if (!authStore.token()) {
        return router.createUrlTree(['/auth/login']);
    }

    return authService.me().pipe(
        timeout({ each: 8000 }),
        map(() => (hasPermissionAccess() ? true : denyAccess())),
        catchError(() => of(router.createUrlTree(['/auth/login'])))
    );
};

/** Redireciona `/` para a primeira tela que o usuário pode acessar. */
export const panelHomeRedirectGuard: CanActivateFn = () => {
    const authStore = inject(AuthStore);
    const authService = inject(AuthService);
    const router = inject(Router);

    if (authStore.isAuthenticated()) {
        return resolveHomeUrlTree(authStore, router);
    }

    if (!authStore.token()) {
        return router.createUrlTree(['/auth/login']);
    }

    return authService.me().pipe(
        timeout({ each: 8000 }),
        map(() => resolveHomeUrlTree(authStore, router)),
        catchError(() => of(router.createUrlTree(['/auth/login'])))
    );
};

export const authGuard: CanActivateFn = (route) => resolveAccessGuard(route.data?.['permissions']);

export const authChildGuard: CanActivateChildFn = (route) => resolveAccessGuard(route.data?.['permissions']);
